import { useAuthorization } from '@autone/auth';
import { useAutoneTranslation } from '@autone/translations';
import {
  createColumnLoadingGuards,
  DataTableCellContent,
  DataTableCellSubtitle,
  DataTableCellTitle,
  type DataTableLoadingObject,
  TailwindProductCell,
  TailwindSkeleton,
} from '@autone/ui';
import {
  DataTableRowCheckbox,
  DataTableSelectAllCheckbox,
} from '@autone/ui-next/components';
import { type ColumnHelper } from '@tanstack/react-table';
import { useAtomValue } from 'jotai';

import { type APP_NAME } from '../../../../consts';
import { useReleaseAsortmentBulkEdits } from '../../../../hooks/feature-flags';
import { type AssortmentSkuLocation } from '../../../../redux/assortment';
import { inSeasonSearchTermAtom } from '../../../../storage/atoms';

const COLUMN_ID = 'sku_product' as const;

export type SkuProductColumnId = typeof COLUMN_ID;

interface SkuProductFields {
  sku: AssortmentSkuLocation['sku'];
}

const { columnCellGuard, accessorFnGuard } =
  createColumnLoadingGuards<SkuProductFields>();

export const createSkuProductColumn = <
  T extends SkuProductFields | DataTableLoadingObject,
>(
  columnHelper: ColumnHelper<T>,
) =>
  columnHelper.accessor(
    accessorFnGuard(
      (row) => row.sku.product.style_desc + ' ' + row.sku.product.style_id,
    ),
    {
      id: COLUMN_ID,
      size: 350,
      header: function ProductNameHeader() {
        const { t } = useAutoneTranslation<typeof APP_NAME>();
        const release = useReleaseAsortmentBulkEdits();
        const searchTerm = useAtomValue(inSeasonSearchTermAtom);
        const { hasReadOnlyAccess } = useAuthorization();

        return (
          <div className="flex flex-row gap-4 items-center">
            {release.skuLocationsTable && (
              <DataTableSelectAllCheckbox
                disabled={searchTerm.length > 0 || hasReadOnlyAccess}
                id={'sku-locations-table-select-all-checkbox'}
              />
            )}
            <DataTableCellContent>
              <DataTableCellTitle className="uppercase font-medium">
                {t('table-columns.product')}
              </DataTableCellTitle>
              <DataTableCellSubtitle>
                {t('table-columns.product-id')}
              </DataTableCellSubtitle>
            </DataTableCellContent>
          </div>
        );
      },
      cell: (ctx) =>
        columnCellGuard({
          ctx,
          renderCell: function ProductNameCell({ row }) {
            const product = row.original.sku.product;
            const release = useReleaseAsortmentBulkEdits();
            const { hasReadOnlyAccess } = useAuthorization();

            return (
              <div className="flex flex-row gap-4 items-center">
                {release.skuLocationsTable && (
                  <DataTableRowCheckbox
                    disabled={hasReadOnlyAccess}
                    rowId={row.id}
                    id={`sku-locations-table-${row.id}-checkbox`}
                  />
                )}
                <TailwindProductCell
                  productId={product.id}
                  styleDesc={product.style_desc}
                  imageUrl={product.image_url}
                  colorDesc={product.color_desc}
                  materialDesc={product.material_desc}
                />
              </div>
            );
          },
          renderLoader: function ProductNameCellLoader() {
            const release = useReleaseAsortmentBulkEdits();

            return (
              <div className="flex flex-row gap-4 h-full w-full items-center">
                {release.skuLocationsTable && (
                  <TailwindSkeleton className="h-4 w-4 rounded-sm" />
                )}
                <TailwindSkeleton className="h-full aspect-[3/2] rounded-sm" />
                <TailwindSkeleton className="flex-1 h-full rounded-sm" />
              </div>
            );
          },
        }),
    },
  );
